/**
 * Library / store shell — the app's home screen.
 *
 * NO CURRENCY UI. The design mock shows tea and diamond counters, and an earlier
 * version of this file rendered them with hardcoded values. That is a store
 * rejection: App Store Guideline 2.1 / 4.2 and Google Play's misrepresentation
 * policy both treat non-functional commerce UI as a defect, and a reviewer
 * cannot distinguish "placeholder" from "broken". The counters come back the day
 * an economy is actually wired — not before. See docs/design/GDD.md §5.
 *
 * The footer nav ships at the client's direction, with the mock's own icons —
 * but only Настройки is live. The other three destinations (shop, video,
 * video) do not exist yet, so those buttons are disabled, not decorative:
 * same rule as the category chips.
 */
import { useRef, useState } from 'react';
import { BottomNav } from './BottomNav.js';
import { loadWallet } from './wallet.js';

export interface BookCard {
  id: string;
  title: string;
  genres: string[];
  synopsis: string;
  cover: string;
  /** Optional looping video cover, shown in the featured slot. */
  motion?: string | undefined;
  status?: string | undefined;
  playable: boolean;
}

/** Library chrome in both catalog languages. Only this screen switches —
    episode scripts exist in Russian alone. The inert categories still need
    data the catalog doesn't carry (popularity, recency); they stay disabled
    rather than silently inert, so nobody reads them as broken. */
const STR = {
  ru: {
    wordmark: 'Высокая кухня',
    favs: 'Избранное',
    cats: ['По жанрам', 'ТОП-10', 'Новинки'],
    catsAria: 'Категории',
    carousel: 'Рекомендуемые истории',
    carouselRole: 'карусель',
    prev: 'Предыдущая история',
    next: 'Следующая история',
    soonSuffix: ' Скоро.',
    soon: 'скоро',
    all: 'Все истории',
    fav: 'В избранное',
    empty: 'Пока пусто — отметьте историю закладкой.',
    nav: 'Разделы',
    shop: 'Магазин',
    video: 'Видео',
    profile: 'Профиль',
    settings: 'Настройки',
    tickets: 'Билеты',
    coins: 'Монеты',
    plusTickets: 'Магазин — билеты',
    plusCoins: 'Магазин — монеты',
    langAria: 'Switch to English',
  },
  en: {
    wordmark: 'High Cuisine',
    favs: 'Favourites',
    cats: ['By genre', 'Top 10', 'New'],
    catsAria: 'Categories',
    carousel: 'Featured stories',
    carouselRole: 'carousel',
    prev: 'Previous story',
    next: 'Next story',
    soonSuffix: ' Coming soon.',
    soon: 'soon',
    all: 'All stories',
    fav: 'Bookmark',
    empty: 'Nothing here yet — bookmark a story.',
    nav: 'Sections',
    shop: 'Shop',
    video: 'Video',
    profile: 'Profile',
    settings: 'Settings',
    tickets: 'Tickets',
    coins: 'Coins',
    plusTickets: 'Shop — tickets',
    plusCoins: 'Shop — coins',
    langAria: 'Переключить на русский',
  },
} as const;

/** Favourites persist locally, same as prefs: a Set in state, an array on disk. */
const FAVS_KEY = 'hon.favs';

/** Virtual slides kept mounted on each side of the centre. Two, so a fast
    double-step still rotates a mounted card in instead of popping one. */
const RANGE = 2;

/** Dragging past this fraction of the viewport width commits the swipe. */
const SWIPE_COMMIT = 0.12;

function loadFavs(): Set<string> {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(FAVS_KEY) ?? '[]');
    return new Set(Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

/** Carousel position class for a slide `off` steps away from the front. */
function slotClass(off: number): string {
  if (off === 0) return 'front';
  if (off === -1) return 'back-l';
  if (off === 1) return 'back-r';
  return off < 0 ? 'hide-l' : 'hide-r';
}

export function Library({
  books,
  lang,
  onLang,
  onOpen,
  onShop,
  onSettings,
  onProfile,
}: {
  books: BookCard[];
  lang: 'ru' | 'en';
  onLang: () => void;
  onOpen: (id: string) => void;
  onShop: (tab?: 'tickets' | 'coins') => void;
  onSettings: () => void;
  onProfile: () => void;
}) {
  const T = STR[lang];
  /* `featured` counts monotonically — rotating forward forever keeps
     increasing it — and mod() maps it onto a book. Slides are keyed by their
     virtual index, so on a step each mounted card animates from its old slot
     to its new one: the side card swings up front, the front card swings down
     and back. That is the rotation. */
  const [featured, setFeatured] = useState(0);
  const [favs, setFavs] = useState<Set<string>>(loadFavs);
  const [onlyFavs, setOnlyFavs] = useState(false);

  /* Read-only here; spending arrives with the economy. See ui/wallet.ts for
     why balances may render while prices and purchases must not. */
  const [wallet] = useState(loadWallet);

  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const zoneW = useRef(1);
  const pointerId = useRef<number | null>(null);
  /** Set once a gesture is a real drag; the cover's click handler checks it so
      the tap that *ends* a swipe doesn't also open the book. */
  const moved = useRef(false);

  const n = books.length;
  const mod = (v: number) => ((v % n) + n) % n;
  const current = n > 0 ? books[mod(featured)] : undefined;
  const slides =
    n > 0
      ? Array.from({ length: RANGE * 2 + 1 }, (_, k) => featured - RANGE + k)
          .map((v) => ({ v, book: books[mod(v)] }))
          .filter((s): s is { v: number; book: BookCard } => s.book !== undefined)
      : [];
  const shown = onlyFavs ? books.filter((b) => favs.has(b.id)) : books;

  const step = (dir: number) => setFeatured((f) => f + dir);

  const toggleFav = (id: string) => {
    const nextFavs = new Set(favs);
    if (!nextFavs.delete(id)) nextFavs.add(id);
    localStorage.setItem(FAVS_KEY, JSON.stringify([...nextFavs]));
    setFavs(nextFavs);
  };

  /* Swipe. Pointer events rather than touch events so a mouse drag works on
     desktop too. Capture is only taken once the gesture passes the drag
     threshold — capturing on pointerdown would re-target the click and break
     plain taps on the cover. */
  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (n < 2 || pointerId.current !== null) return;
    pointerId.current = e.pointerId;
    setDragging(true);
    moved.current = false;
    startX.current = e.clientX;
    zoneW.current = e.currentTarget.clientWidth || 1;
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || e.pointerId !== pointerId.current) return;
    const dx = e.clientX - startX.current;
    if (!moved.current && Math.abs(dx) > 8) {
      moved.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    if (moved.current) setDrag(dx);
  };
  const onUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerId !== pointerId.current) return;
    pointerId.current = null;
    setDragging(false);
    const commit = zoneW.current * SWIPE_COMMIT;
    if (drag <= -commit) step(1);
    else if (drag >= commit) step(-1);
    setDrag(0);
  };

  return (
    <div className="library">
      <header className="lib-top">
        <p className="wordmark">{T.wordmark}</p>
        {/* The "+" deep-links to the matching shop tab. Visually 16px like the
            mock; the hit area is padded to the 48px floor in CSS. */}
        <span className="pill tickets" aria-label={`${T.tickets}: ${wallet.tickets}`}>
          <img src="hud/ticket.svg" alt="" draggable={false} />
          <span className="count">{wallet.tickets}</span>
          <button className="plus" onClick={() => onShop('tickets')} aria-label={T.plusTickets}>+</button>
        </span>
        <span className="pill coins" aria-label={`${T.coins}: ${wallet.coins}`}>
          <img src="hud/coin.svg" alt="" draggable={false} />
          <span className="count">{wallet.coins}</span>
          <button className="plus" onClick={() => onShop('coins')} aria-label={T.plusCoins}>+</button>
        </span>
        <span className="lib-spacer" />
        {/* Mobile reaches settings through the footer gear, as in the mock;
            this header gear only exists on desktop, where the footer is
            hidden. No sound toggle: the motion covers ship without audio
            tracks — it returns the day sounded covers land. */}
        <button className="icon-btn shop-btn" onClick={() => onShop()} aria-label={T.shop}>
          <span className="nav-ico ico-cart" />
        </button>
        <button className="icon-btn set-btn" onClick={onSettings} aria-label={T.settings}>
          <span className="nav-ico ico-gear" />
        </button>
        {/* The mock's globe ("language button top" in the file), doing what
            its name says: a real RU/EN switch over the bilingual catalog. */}
        <button className="lang-btn" onClick={onLang} aria-label={T.langAria}>
          <span className="nav-ico ico-globe" />
        </button>
      </header>

      <div
        className="feat-zone"
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
        }}
      >
        <div
          className={`feat-viewport${dragging ? ' dragging' : ''}`}
          style={{ '--drag': `${drag}px` } as React.CSSProperties}
          aria-roledescription={T.carouselRole}
          aria-label={T.carousel}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          {slides.map(({ v, book }) => {
            const front = v === featured;
            return (
              <div key={v} className={`feat-slide ${slotClass(v - featured)}`} aria-hidden={!front}>
                {book.motion && front ? (
                  // muted + playsInline so iOS/Android WebViews will autoplay it.
                  <video
                    src={book.motion}
                    poster={book.cover}
                    autoPlay
                    loop
                    muted
                    playsInline
                    aria-hidden="true"
                  />
                ) : (
                  <img src={book.cover} alt="" draggable={false} />
                )}
              </div>
            );
          })}
        </div>

        {current?.status && <span className="badge">{current.status}</span>}

        <button
          className="feat-hit"
          onClick={() => {
            if (moved.current) { moved.current = false; return; }
            if (current) onOpen(current.id);
          }}
          disabled={!current?.playable}
          aria-label={
            current
              ? `${current.title}. ${current.genres.join(', ')}.${current.playable ? '' : T.soonSuffix}`
              : ''
          }
        />

        {n > 1 && (
          <>
            <button className="arrow left" onClick={() => step(-1)} aria-label={T.prev} />
            <button className="arrow right" onClick={() => step(1)} aria-label={T.next} />
          </>
        )}
      </div>

      {/* Chips sit directly under the carousel, as in the mock; the featured
          book's meta follows them. */}
      <nav className="chips" aria-label={T.catsAria}>
        <button
          className={`chip${onlyFavs ? ' on' : ''}`}
          aria-pressed={onlyFavs}
          onClick={() => setOnlyFavs((v) => !v)}
        >
          {T.favs}
        </button>
        {T.cats.map((c) => (
          <button key={c} className="chip" disabled aria-disabled="true">{c}</button>
        ))}
      </nav>

      {current && (
        <div className="feat-meta">
          <h2>{current.title}</h2>
          <p className="genres">{current.genres.join(' • ')}</p>
          <p className="synopsis">{current.synopsis}</p>
        </div>
      )}

      <section className="grid" aria-label={T.all}>
        {shown.map((b) => (
          <div key={b.id} className="card-wrap">
            <button
              className={`card${b.playable ? '' : ' locked'}`}
              onClick={() => onOpen(b.id)}
              disabled={!b.playable}
              aria-label={`${b.title}.${b.playable ? '' : T.soonSuffix}`}
            >
              <img src={b.cover} alt="" draggable={false} />
              {!b.playable && <span className="soon">{T.soon}</span>}
            </button>
            <button
              className={`fav${favs.has(b.id) ? ' on' : ''}`}
              aria-pressed={favs.has(b.id)}
              aria-label={`${T.fav}: ${b.title}`}
              onClick={() => toggleFav(b.id)}
            />
          </div>
        ))}
        {onlyFavs && shown.length === 0 && (
          <p className="grid-empty">{T.empty}</p>
        )}
      </section>

      <BottomNav lang={lang} onShop={() => onShop()} onProfile={onProfile} onSettings={onSettings} />
    </div>
  );
}
